import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { buildStep2Schema } from './validationSchemas';
import signupFlowSteps from './signupFlowConfig';
import {
  getAdministrativeLevelMetadata,
  getCountryMetaData,
  postalCodeLookup,
} from './login.service';
import SignupProgress from './signupProgress';
import { authHeadingStyles, authSubtextStyles } from './signupTheme';

const fieldLabelStyles = {
  color: 'text.primary',
  fontWeight: 700,
};

const fieldGroupStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

// Postal-code lookup guards, used only when the country has no configured postal-code
// pattern to tell us when the value is complete. Every lookup is a billed Google
// Geocoding call, so a prefix that cannot plausibly be a postal code is not worth one.
const MIN_UNPATTERNED_POSTAL_CODE_LENGTH = 3;
const POSTAL_CODE_LOOKUP_DEBOUNCE_MS = 600;

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// A selected level holds a location id (UUID); a manually entered new location
// holds its plain name. This distinguishes the two.
const isUuidValue = (value) => typeof value === 'string' && UUID_REGEX.test(value);

// Levels 1-2 (state, district) are closed lists, as GeoQueryService.CLOSED_LIST_MAX_LEVEL.
const CLOSED_LIST_MAX_LEVEL = 2;

/**
 * States and districts are a closed list once a country has them: the backend refuses a typed
 * name that matches none when the sign-up is submitted. Same check (and message) on this step,
 * so the member can fix it here instead of coming back from the last one. Returns null when the
 * value is fine: an id picked from the list, a name matching an option, or a level with no
 * options loaded (still open to typing, as on the backend).
 */
export const closedLevelMessage = ({ level, value, options = [], levels = [], parentSelected = false }) => {
  if (level > CLOSED_LIST_MAX_LEVEL || !value || isUuidValue(value)) {
    return null;
  }
  const typed = String(value).trim();
  if (
    !options?.length ||
    options.some((option) => (option.name || '').trim().toLowerCase() === typed.toLowerCase())
  ) {
    return null;
  }
  const levelName = levels?.find((candidate) => candidate.level === level)?.name || 'location';
  const parentName = levels?.find((candidate) => candidate.level === level - 1)?.name;
  return `"${typed}" isn't a known ${levelName.toLowerCase()}${
    level > 1 && parentName && parentSelected ? ` in the selected ${parentName.toLowerCase()}` : ''
  }. Pick one from the list.`;
};

// Extract maximum length from regex pattern
const extractMaxLengthFromPattern = (pattern) => {
  if (!pattern) return null;

  try {
    // Remove anchors
    const cleanPattern = pattern.replace(/^\^/, '').replace(/\$$/, '');

    let totalLength = 0;
    let index = 0;

    while (index < cleanPattern.length) {
      const char = cleanPattern[index];

      // Handle escape sequences with quantifiers: \d{7}, \w{3}, etc.
      if (char === '\\' && index + 1 < cleanPattern.length) {
        const nextChar = cleanPattern[index + 1];
        if (nextChar === 'd' || nextChar === 'w' || nextChar === 's' || nextChar === 'D' || nextChar === 'W' || nextChar === 'S') {
          // Check for quantifier after the escape sequence
          if (index + 2 < cleanPattern.length && cleanPattern[index + 2] === '{') {
            const endBrace = cleanPattern.indexOf('}', index + 2);
            if (endBrace > index + 2) {
              const quantifier = cleanPattern.substring(index + 3, endBrace);
              if (/^\d+$/.test(quantifier)) {
                totalLength += parseInt(quantifier, 10);
                index = endBrace + 1;
                continue;
              }
            }
          }
          // No quantifier, just a single character
          totalLength += 1;
          index += 2;
          continue;
        }
        // Literal escaped character
        totalLength += 1;
        index += 2;
        continue;
      }

      // Handle quantified patterns: [A-Z]{n}, etc. (non-escaped)
      if (index + 1 < cleanPattern.length && cleanPattern[index + 1] === '{') {
        const endBrace = cleanPattern.indexOf('}', index);
        if (endBrace > index) {
          const quantifier = cleanPattern.substring(index + 2, endBrace);
          if (/^\d+$/.test(quantifier)) {
            totalLength += parseInt(quantifier, 10);
            index = endBrace + 1;
            continue;
          }
        }
      }

      // Handle character classes [A-Z]
      if (char === '[') {
        const endBracket = cleanPattern.indexOf(']', index);
        if (endBracket > index) {
          totalLength += 1;
          index = endBracket + 1;
          continue;
        }
      }

      // Handle parentheses (groups) - don't count as characters
      if (char === '(' || char === ')') {
        index += 1;
        continue;
      }

      // Skip special regex chars that don't add length
      if ('|?+*^$'.includes(char)) {
        index += 1;
        continue;
      }

      // Literal character
      totalLength += 1;
      index += 1;
    }

    return totalLength || null;
  } catch (e) {
    return null;
  }
};

// Build a regex object from pattern string
const buildRegexFromPattern = (pattern) => {
  if (!pattern) return null;

  try {
    // Create regex from pattern string
    // Patterns are stored as regex strings like "^\d{4}$" or "^[A-Z]\d{3}$"
    return new RegExp(pattern);
  } catch (e) {
    return null;
  }
};

// Validate postal code against the pattern
const validatePostalCodePattern = (value, pattern) => {
  if (!pattern || !value) return true; // No pattern or empty value means valid

  const regex = buildRegexFromPattern(pattern);
  if (!regex) return true; // Invalid pattern, allow input

  return regex.test(value);
};

// Extract allowed characters from regex pattern for input filtering
const getAllowedCharactersFromPattern = (pattern) => {
  if (!pattern) return /./; // Allow any character if no pattern

  try {
    // Build a set of allowed characters by analyzing the pattern
    const allowedChars = new Set();

    // Remove anchors and alternation groups temporarily to analyze
    const cleanPattern = pattern.replace(/^\^/, '').replace(/\$$/, '');

    // Extract all character classes and individual characters
    const charClassRegex = /\[([^\]]+)\]|\\d|\\w|\\s|[^\[\]\\]/g;
    let match;

    while ((match = charClassRegex.exec(cleanPattern)) !== null) {
      const token = match[0];

      if (token === '\\d') {
        // Digit character class
        for (let i = 48; i <= 57; i++) allowedChars.add(String.fromCharCode(i)); // 0-9
      } else if (token === '\\w') {
        // Word character class
        for (let i = 65; i <= 90; i++) allowedChars.add(String.fromCharCode(i)); // A-Z
        for (let i = 97; i <= 122; i++) allowedChars.add(String.fromCharCode(i)); // a-z
        for (let i = 48; i <= 57; i++) allowedChars.add(String.fromCharCode(i)); // 0-9
        allowedChars.add('_');
      } else if (token === '\\s') {
        // Whitespace character class
        allowedChars.add(' ');
        allowedChars.add('\t');
        allowedChars.add('\n');
      } else if (token.startsWith('[')) {
        // Character class [...]
        const charClass = token.slice(1, -1);
        // Handle ranges like A-Z or a-z
        const rangeRegex = /(.)-(.)/g;
        let rangeMatch;
        const rangeStart = charClass.replace(rangeRegex, (m, start, end) => {
          for (let i = start.charCodeAt(0); i <= end.charCodeAt(0); i++) {
            allowedChars.add(String.fromCharCode(i));
          }
          return '';
        });

        // Add remaining individual characters (not part of ranges)
        for (const char of rangeStart) {
          if (char !== '-' && char !== '\\') {
            allowedChars.add(char);
          }
        }
      } else if (token !== '\\' && token !== '|' && token !== '(' && token !== ')' && token !== '?' && token !== '+' && token !== '*' && token !== '{' && token !== '}') {
        // Literal character
        allowedChars.add(token);
      }
    }

    // If we extracted some characters, create a pattern from them
    if (allowedChars.size > 0) {
      const escapedChars = Array.from(allowedChars)
        .map(char => {
          if (/[\\^$.*+?()[\]{}|-]/.test(char)) {
            return '\\' + char;
          }
          return char;
        })
        .join('');
      return new RegExp(`[${escapedChars}]`);
    }

    // Default to alphanumeric with space
    return /[A-Za-z0-9 ]/;
  } catch (e) {
    // Fallback to alphanumeric with space
    return /[A-Za-z0-9 ]/;
  }
};

// Convert regex pattern to user-friendly explanation
const getPatternExplanation = (pattern) => {
  if (!pattern) return '';

  try {
    // Common simple patterns only
    if (/^\d{4}$/.test(pattern)) return '4 digits';
    if (/^\d{5}$/.test(pattern)) return '5 digits';
    if (/^\d{6}$/.test(pattern)) return '6 digits';
    if (/^\d{7}$/.test(pattern)) return '7 digits';
    if (/^[A-Z]\d{3}$/.test(pattern)) return 'Letter followed by 3 digits';
    if (/^[A-Z]{2}\d{5}$/.test(pattern)) return '2 letters followed by 5 digits';
    if (/^\d{5}-\d{4}$/.test(pattern)) return '5 digits, dash, 4 digits';

    // For complex patterns, return empty to avoid inaccurate explanations
    // Users should rely on the hint field for complex formats
    return '';
  } catch (e) {
    return '';
  }
};

// Map address template levels to generic form field names
const getLevelFieldMap = (levels) => {
  const map = {};
  levels.forEach(level => {
    map[level.level] = `level${level.level}`;
  });
  return map;
};

// Get parent ID (direct parent) for a given level
const getParentLevelIds = (formData, levelFieldMap, level) => {
  if (level <= 1) {
    return null;
  }
  // Return the direct parent (level - 1)
  const parentLevel = level - 1;
  const parentFieldName = levelFieldMap[parentLevel];
  return (parentFieldName && formData[parentFieldName]) || null;
};

// Default template used as fallback when backend template is not available
const getDefaultAddressTemplate = () => ({
  levels: [
    { level: 1, name: 'Level 1', required: true },
    { level: 2, name: 'Level 2', required: true },
    { level: 3, name: 'Level 3', required: true },
    { level: 4, name: 'Level 4', required: true },
  ],
  postalCode: {
    name: 'Postal Code',
    length: null,
    format: 'numeric',
    required: true,
    pattern: null,
    hint: 'Enter postal code',
  },
});

export default function SignupStep2({
  handleContinue,
  handleBack,
  handleChange,
  formData = {},
}) {
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [countryOptions, setCountryOptions] = useState([]);
  const [levelOptions, setLevelOptions] = useState({});
  const [postalCodeLevelData, setPostalCodeLevelData] = useState({});
  const [loadAllLevelsAfterPostalCode, setLoadAllLevelsAfterPostalCode] = useState(false);
  const [levelLoadingStates, setLevelLoadingStates] = useState({});

  // Resolve the address template by matching the country selected in Step 1
  // (read-only in Step 2) against the /auth/metadata response. Falls back to the
  // template Step 1 stored, then to the generic default while metadata loads.
  const addressTemplate = useMemo(() => {
    const matchedCountry = countryOptions.find(
      (country) =>
        String(country.countryManagement?.id) === String(formData?.administrativeRegion)
    );

    return (
      matchedCountry?.addressTemplate ||
      formData?.addressTemplate ||
      getDefaultAddressTemplate()
    );
  }, [countryOptions, formData?.administrativeRegion, formData?.addressTemplate]);

  const levelFieldMap = useMemo(() => {
    return addressTemplate ? getLevelFieldMap(addressTemplate.levels || []) : {};
  }, [addressTemplate]);

  // Validation tracks the country's template: the last level is always optional.
  const step2ValidationSchema = useMemo(
    () => buildStep2Schema(addressTemplate?.levels || []),
    [addressTemplate]
  );

  const selectedCountryName = useMemo(() => {
    const selectedCountry = countryOptions.find(
      (country) => String(country.countryManagement?.id) === String(formData?.administrativeRegion)
    );

    return selectedCountry?.countryManagement?.name || '';
  }, [countryOptions, formData?.administrativeRegion]);

  const mergedLevelOptions = useMemo(() => {
    const merged = { ...levelOptions };

    // Merge postal code lookup data with metadata options
    Object.entries(postalCodeLevelData).forEach(([levelNum, data]) => {
      if (data && data.id) {
        merged[levelNum] = [data, ...(merged[levelNum] || [])];
      }
    });

    // Remove duplicates by id within each level
    Object.keys(merged).forEach(level => {
      const uniqueMap = new Map();
      merged[level].forEach(opt => {
        if (opt.id && !uniqueMap.has(opt.id)) {
          uniqueMap.set(opt.id, opt);
        }
      });
      merged[level] = Array.from(uniqueMap.values());
    });

    return merged;
  }, [levelOptions, postalCodeLevelData]);

  // Level number for each level field ('state' -> 1, ...), the inverse of levelFieldMap.
  const levelByField = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(levelFieldMap).map(([level, fieldName]) => [fieldName, Number(level)])
      ),
    [levelFieldMap]
  );

  const closedLevelError = (level, value) =>
    closedLevelMessage({
      level,
      value,
      options: mergedLevelOptions[level],
      levels: addressTemplate?.levels,
      parentSelected: Boolean(formData?.[levelFieldMap[level - 1]]),
    });

  const postalCodePattern = addressTemplate?.postalCode?.pattern;
  const extractedMaxLength = useMemo(
    () => postalCodePattern ? extractMaxLengthFromPattern(postalCodePattern) : null,
    [postalCodePattern]
  );

  const isPostalCodeComplete = extractedMaxLength
    ? (formData?.pincode || '').length === extractedMaxLength
    : Boolean(formData?.pincode && postalCodePattern ? validatePostalCodePattern(formData.pincode, postalCodePattern) : formData?.pincode);


  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getCountryMetaData();
        if (response.statusCode === 200) {
          // Keep full items ({ countryManagement, addressTemplate }) so the address
          // template can be matched to the selected country.
          setCountryOptions(response.data);
          return;
        }
      } catch (error) {
        // Ignore and fall back to an empty list.
      }

      setCountryOptions([]);
    };

    loadCountries();
  }, []);

  useEffect(() => {
    if (!formData?.administrativeRegion || !countryOptions.length) return;

    const selectedCountry = countryOptions.find(
      (country) => String(country.countryManagement?.id) === String(formData.administrativeRegion)
    );

    const matchedTemplate = selectedCountry?.addressTemplate;
    if (matchedTemplate && matchedTemplate !== formData?.addressTemplate) {
      handleChange({
        target: {
          name: 'addressTemplate',
          value: matchedTemplate,
        },
      });
    }
  }, [formData?.administrativeRegion, countryOptions, handleChange, formData?.addressTemplate]);

  const prevFormDataRef = useRef({});
  // Tracks the most recent postal code so a slow lookup response for a previous
  // value can't repopulate levels after the field has changed again.
  const latestPincodeRef = useRef('');
  const pincodeLookupTimerRef = useRef(null);

  // Don't leave a debounced lookup pending after the step unmounts.
  useEffect(() => () => {
    if (pincodeLookupTimerRef.current) {
      clearTimeout(pincodeLookupTimerRef.current);
    }
  }, []);

  // Initialize prevFormDataRef on mount to prevent false change detection
  useEffect(() => {
    prevFormDataRef.current = { ...formData };
  }, []);

  // The outermost administrative level is keyed only by country. Load it as soon as the
  // country is known — otherwise the dropdowns stay empty until a postal-code lookup
  // happens to fill them, and the user can only type.
  useEffect(() => {
    if (!formData.administrativeRegion || !addressTemplate?.levels?.length) {
      return;
    }
    if (loadAllLevelsAfterPostalCode) {
      return;
    }

    let cancelled = false;

    const loadRootLevel = async () => {
      setLevelLoadingStates((prev) => ({ ...prev, 1: true }));
      try {
        const response = await getAdministrativeLevelMetadata(
          1,
          formData.administrativeRegion,
          null
        );
        if (cancelled) {
          return;
        }
        setLevelOptions((prev) => ({
          ...prev,
          1: response.statusCode === 200 ? response.data || [] : [],
        }));
      } finally {
        if (!cancelled) {
          setLevelLoadingStates((prev) => ({ ...prev, 1: false }));
        }
      }
    };

    loadRootLevel();
    return () => {
      cancelled = true;
    };
  }, [formData.administrativeRegion, addressTemplate?.levels, loadAllLevelsAfterPostalCode]);

  // Load all level options if form already has data (e.g., navigating back from next step)
  useEffect(() => {
    if (!addressTemplate?.levels || !formData.administrativeRegion) return;

    // Check if any level has a value but levelOptions is empty
    const hasLevelValues = addressTemplate.levels.some(level => {
      const fieldName = levelFieldMap[level.level];
      return formData[fieldName];
    });

    const hasNoOptions = Object.keys(levelOptions).length === 0;

    if (hasLevelValues && hasNoOptions) {
      setLoadAllLevelsAfterPostalCode(true);
    }
  }, []);

  // Load all level metadata after postal code lookup succeeds
  useEffect(() => {
    if (!loadAllLevelsAfterPostalCode || !addressTemplate?.levels) return;

    const loadAllLevels = async () => {
      // Mark all levels as loading
      const loadingStates = {};
      addressTemplate.levels.forEach(level => {
        loadingStates[level.level] = true;
      });
      setLevelLoadingStates(loadingStates);

      // Add delay to allow backend to save postal code data
      await new Promise(resolve => setTimeout(resolve, 500));

      const newLevelOptions = {};

      for (const level of addressTemplate.levels) {
        // Check if all parent levels have values
        let parentLevelsComplete = true;
        for (let i = 1; i < level.level; i++) {
          const parentFieldName = levelFieldMap[i];
          if (!parentFieldName || !formData[parentFieldName]) {
            parentLevelsComplete = false;
            break;
          }
        }

        if (!parentLevelsComplete) {
          newLevelOptions[level.level] = [];
          setLevelLoadingStates(prev => ({ ...prev, [level.level]: false }));
          continue;
        }

        // Get parent IDs for this level
        const parentIds = getParentLevelIds(formData, levelFieldMap, level.level);

        // Same guard as the next-level loader below: a manually entered new location holds its
        // plain name, not a UUID id, and has no children to load. Without this the name went out
        // as ?parentId=, and the backend rejected it — "Invalid UUID string: Bomet Central".
        if (parentIds && !isUuidValue(parentIds)) {
          newLevelOptions[level.level] = [];
          setLevelLoadingStates(prev => ({ ...prev, [level.level]: false }));
          continue;
        }

        // Fetch metadata for this level
        const response = await getAdministrativeLevelMetadata(
          level.level,
          formData.administrativeRegion,
          parentIds
        );

        newLevelOptions[level.level] = response.statusCode === 200 ? response.data || [] : [];
        setLevelLoadingStates(prev => ({ ...prev, [level.level]: false }));
      }

      setLevelOptions(newLevelOptions);
      setLoadAllLevelsAfterPostalCode(false);
      prevFormDataRef.current = { ...formData };
    };

    loadAllLevels();
  }, [loadAllLevelsAfterPostalCode, addressTemplate.levels, levelFieldMap, formData]);

  // Dynamically load metadata for next level when any level changes (only after postal code bulk load is done)
  useEffect(() => {
    const loadNextLevelMetadata = async () => {
      // Skip if bulk load is in progress
      if (loadAllLevelsAfterPostalCode) return;

      if (!formData.administrativeRegion) {
        setLevelOptions({});
        return;
      }

      // Initialize prevFormDataRef on first run if empty
      if (!prevFormDataRef.current || Object.keys(prevFormDataRef.current).length === 0) {
        prevFormDataRef.current = { ...formData };
        return;
      }

      // Determine which level changed by comparing with previous state
      let changedLevel = null;
      const prevFormData = prevFormDataRef.current;

      if (prevFormData.administrativeRegion !== formData.administrativeRegion) {
        changedLevel = 0; // Country changed, load level 1
      } else {
        for (const level of addressTemplate.levels) {
          const fieldName = levelFieldMap[level.level];
          if (fieldName && prevFormData[fieldName] !== formData[fieldName]) {
            changedLevel = level.level;
            break;
          }
        }
      }

      // If no level changed, return
      if (changedLevel === null) return;

      // Find the next level to load
      const nextLevel = addressTemplate.levels.find(l => l.level === changedLevel + 1);

      if (!nextLevel) {
        // No next level to load, clear all levels after changed level
        const newOptions = { ...levelOptions };
        for (const level of addressTemplate.levels) {
          if (level.level > changedLevel) {
            newOptions[level.level] = [];
          }
        }
        setLevelOptions(newOptions);
        prevFormDataRef.current = { ...formData };
        return;
      }

      // Get parent IDs for next level
      const parentIds = getParentLevelIds(formData, levelFieldMap, nextLevel.level);

      // If the parent is a manually-entered new location (a name, not a UUID id),
      // it has no children to load from the backend. Clear options so the user can
      // add the child manually too.
      if (parentIds && !isUuidValue(parentIds)) {
        setLevelOptions(prev => ({ ...prev, [nextLevel.level]: [] }));
        setLevelLoadingStates(prev => ({ ...prev, [nextLevel.level]: false }));
        prevFormDataRef.current = { ...formData };
        return;
      }

      // Check if all parent levels have values
      let parentLevelsComplete = true;
      for (let i = 1; i < nextLevel.level; i++) {
        const parentFieldName = levelFieldMap[i];
        if (!parentFieldName || !formData[parentFieldName]) {
          parentLevelsComplete = false;
          break;
        }
      }

      if (!parentLevelsComplete) {
        setLevelOptions(prev => ({ ...prev, [nextLevel.level]: [] }));
        prevFormDataRef.current = { ...formData };
        return;
      }

      // Mark next level as loading
      setLevelLoadingStates(prev => ({ ...prev, [nextLevel.level]: true }));

      // Fetch metadata for next level
      const response = await getAdministrativeLevelMetadata(
        nextLevel.level,
        formData.administrativeRegion,
        parentIds
      );

      setLevelOptions(prev => ({
        ...prev,
        [nextLevel.level]: response.statusCode === 200 ? response.data || [] : []
      }));

      setLevelLoadingStates(prev => ({ ...prev, [nextLevel.level]: false }));
      prevFormDataRef.current = { ...formData };
    };

    loadNextLevelMetadata();
  }, [formData, addressTemplate.levels, levelFieldMap, loadAllLevelsAfterPostalCode]);

  const setFieldValue = (name, value) => {
    handleChange({
      target: {
        name,
        value,
      },
    });
  };

  const setFieldTouched = (name) => {
    setTouched((currentTouched) => ({ ...currentTouched, [name]: true }));
  };

  const setFieldError = (name, message) => {
    setErrors((currentErrors) => ({ ...currentErrors, [name]: message }));
  };

  const clearFieldError = (name) => {
    setErrors((currentErrors) => {
      if (!currentErrors[name]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[name];
      return nextErrors;
    });
  };

  const validateField = async (name, value, overrides = {}) => {
    try {
      await step2ValidationSchema.validateAt(name, {
        ...formData,
        ...overrides,
        [name]: value,
      });

      // Custom postal code validation
      if (name === 'pincode' && value) {
        const pattern = addressTemplate?.postalCode?.pattern;

        // Only validate format if postal code appears complete
        if (pattern) {
          const isComplete = validatePostalCodePattern(value, pattern);
          if (!isComplete) {
            // Don't show error for incomplete input, only clear if it was erroring before
            clearFieldError(name);
            return;
          }
        }
      }

      const closedError = levelByField[name] ? closedLevelError(levelByField[name], value) : null;
      if (closedError) {
        setFieldError(name, closedError);
        return;
      }

      clearFieldError(name);
    } catch (error) {
      setFieldError(name, error.message);
    }
  };

  const validateStep = async () => {
    const nextErrors = {};
    try {
      await step2ValidationSchema.validate(formData, { abortEarly: false });
    } catch (validationError) {
      (validationError.inner || []).forEach((error) => {
        if (error.path && !nextErrors[error.path]) {
          nextErrors[error.path] = error.message;
        }
      });
    }
    Object.entries(levelFieldMap).forEach(([level, fieldName]) => {
      const closedError = nextErrors[fieldName] ? null : closedLevelError(Number(level), formData?.[fieldName]);
      if (closedError) {
        nextErrors[fieldName] = closedError;
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleTextFieldChange = (name) => (event) => {
    const value = event.target.value;
    setFieldValue(name, value);
    setFieldTouched(name);
    validateField(name, value);
  };

  const handlePostalCodeChange = (event) => {
    const pattern = addressTemplate?.postalCode?.pattern;
    const maxLength = extractedMaxLength;

    // Get allowed character pattern from backend regex
    const allowedCharPattern = getAllowedCharactersFromPattern(pattern);

    // Filter input to only allowed characters, respecting max length
    let filteredValue = '';
    for (const char of event.target.value) {
      // Stop if we've reached max length
      if (maxLength && filteredValue.length >= maxLength) {
        break;
      }
      // Only add character if it matches the pattern
      if (allowedCharPattern.test(char)) {
        filteredValue += char;
      }
    }

    const value = filteredValue;

    setFieldValue('pincode', value);
    setFieldTouched('pincode');
    validateField('pincode', value);
    latestPincodeRef.current = value;

    // Any change to the postal code invalidates previously resolved/prefilled
    // administrative levels — clear them (and lookup-derived options) before
    // re-resolving.
    clearLevelsFrom(1);
    setPostalCodeLevelData({});

    // A lookup in flight is now stale whichever way this change goes.
    if (pincodeLookupTimerRef.current) {
      clearTimeout(pincodeLookupTimerRef.current);
      pincodeLookupTimerRef.current = null;
    }

    // Consider the postal code complete when it matches the pattern, or (when no
    // pattern) when it has enough content to be worth resolving. A pasted full value
    // arrives as a single change event, so this path covers both typing and pasting.
    //
    // The no-pattern branch used to accept any non-empty value, so a country with no
    // configured postal-code pattern fired a lookup on EVERY keystroke: typing 713156
    // sent 7, 71, 713, 7131, 71315, 713156 — six billed Google Geocoding calls, five of
    // them guaranteed to resolve nothing, and five backend warnings. Hence the minimum
    // length plus the debounce below.
    const isComplete = pattern
      ? validatePostalCodePattern(value, pattern)
      : value.trim().length >= MIN_UNPATTERNED_POSTAL_CODE_LENGTH;

    if (!isComplete) {
      setLevelLoadingStates({});
      return;
    }

    // Show spinners immediately when postal code is complete
    const loadingStates = {};
    addressTemplate?.levels?.forEach(level => {
      loadingStates[level.level] = true;
    });
    setLevelLoadingStates(loadingStates);

    // With a pattern, "complete" means the value is genuinely final, so resolve at once.
    // Without one, wait for typing to settle — otherwise every prefix long enough to pass
    // the minimum is still its own lookup.
    if (pattern) {
      runPostalCodeLookup(value);
    } else {
      pincodeLookupTimerRef.current = setTimeout(() => {
        pincodeLookupTimerRef.current = null;
        if (latestPincodeRef.current === value) {
          runPostalCodeLookup(value);
        }
      }, POSTAL_CODE_LOOKUP_DEBOUNCE_MS);
    }
  };

  const runPostalCodeLookup = (value) => {
    const lookupPromise = postalCodeLookup(value, formData.administrativeRegion);

    lookupPromise
      .then((response) => {
        // Ignore responses for a postal code that is no longer current.
        if (latestPincodeRef.current !== value) {
          return;
        }
        if (response && response.found && Array.isArray(response.levels) && addressTemplate?.levels) {
          const postalCodeData = {};

          addressTemplate.levels.forEach(level => {
            const levelData = response.levels.find((l) => l.level === level.level);
            if (levelData && levelData.id) {
              postalCodeData[level.level] = { id: levelData.id, name: levelData.longText };
            }
          });

          setPostalCodeLevelData(postalCodeData);

          setTimeout(() => {
            addressTemplate.levels.forEach(level => {
              const fieldName = levelFieldMap[level.level];
              const levelData = response.levels.find((l) => l.level === level.level);
              if (fieldName && levelData) {
                // Prefer the location id so the dropdown can preselect it. Fall back to the
                // name for a level the lookup resolved only as text.
                setFieldValue(fieldName, levelData.id || levelData.longText || '');
                if (levelData.longText) {
                  setFieldValue(`${fieldName}Name`, levelData.longText);
                }
                clearFieldError(fieldName);
              }
            });
            // Trigger loading all level metadata after postal code levels are populated
            setLoadAllLevelsAfterPostalCode(true);
          }, 0);
        } else {
          // Not found — levels were already cleared above.
          setPostalCodeLevelData({});
          setLevelLoadingStates({});
        }
      })
      .catch(() => {
        if (latestPincodeRef.current !== value) {
          return;
        }
        setPostalCodeLevelData({});
        setLevelLoadingStates({});
      });
  };

  // Clear the value (and display name) of every level at/after startLevel.
  const clearLevelsFrom = (startLevel) => {
    for (let i = startLevel; i <= (addressTemplate?.levels?.length || 0); i++) {
      const fieldName = levelFieldMap[i];
      if (fieldName) {
        setFieldValue(fieldName, '');
        setFieldValue(`${fieldName}Name`, '');
        clearFieldError(fieldName);
      }
    }
  };

  // Stores a level's value. `value` is a location id (when picked from the list)
  // or a typed name (when entered manually — the backend creates the location on
  // signup, for blocks and below; see closedLevelMessage). `displayName` is what the field shows. Children are reset since the
  // parent changed.
  const setLevelValue = (level, value, displayName) => {
    const fieldName = levelFieldMap[level];

    setFieldValue(fieldName, value);
    setFieldValue(`${fieldName}Name`, displayName);
    setFieldTouched(fieldName);
    validateField(fieldName, value);

    clearLevelsFrom(level + 1);
  };

  // Fired when an option is picked, a typed value is confirmed, or the field is cleared.
  const handleLevelSelect = (level) => (event, newValue) => {
    if (newValue == null) {
      setLevelValue(level, '', '');
    } else if (typeof newValue === 'string') {
      const name = newValue.trim();
      setLevelValue(level, name, name);
    } else {
      // Existing option object { id, name } selected from the list.
      setLevelValue(level, newValue.id, newValue.name);
    }
  };

  // Fired as the user types. The input is display-only until they pick an option or
  // confirm a new name (onChange). Committing on every keystroke used to wipe the
  // child levels while they were still filtering the list.
  const handleLevelInput = (level) => (event, newInputValue, reason) => {
    const fieldName = levelFieldMap[level];
    if (reason === 'input') {
      setFieldValue(`${fieldName}Name`, newInputValue);
    } else if (reason === 'clear') {
      setLevelValue(level, '', '');
    }
  };

  const resolveLevelAutocompleteValue = (level, fieldName, options) => {
    const storedValue = formData?.[fieldName] || '';
    const displayName = formData?.[`${fieldName}Name`] || '';

    if (!storedValue) {
      return null;
    }

    const matchedOption = options.find((option) => String(option.id) === String(storedValue));
    if (matchedOption) {
      return matchedOption;
    }

    if (isUuidValue(storedValue)) {
      return { id: storedValue, name: displayName || storedValue };
    }

    return storedValue;
  };

  // A name typed but not picked from the list is only committed when the field
  // loses focus, so filtering the dropdown does not wipe the levels below it.
  const handleLevelBlur = (level, fieldName, options) => () => {
    const displayName = (formData?.[`${fieldName}Name`] || '').trim();
    const storedValue = formData?.[fieldName] || '';

    if (!displayName) {
      if (storedValue) {
        setLevelValue(level, '', '');
      }
      return;
    }

    const matchedOption = options.find(
      (option) => option.name.trim().toLowerCase() === displayName.toLowerCase()
    );
    if (matchedOption) {
      if (String(storedValue) !== String(matchedOption.id)) {
        setLevelValue(level, matchedOption.id, matchedOption.name);
      }
      return;
    }

    if (storedValue !== displayName) {
      setLevelValue(level, displayName, displayName);
    }
  };

  const handleContinueClick = async (event) => {
    event.preventDefault();

    if (!addressTemplate) {
      return;
    }

    // Mark the actual rendered fields as touched so any validation errors
    // (e.g. a required administrative level left empty) become visible.
    const levelFields = (addressTemplate.levels || [])
      .map((level) => levelFieldMap[level.level])
      .filter(Boolean);
    const fieldsToTouch = ['addressLine1', 'pincode', ...levelFields];

    setTouched((currentTouched) => ({
      ...currentTouched,
      ...fieldsToTouch.reduce(
        (fieldState, fieldName) => ({ ...fieldState, [fieldName]: true }),
        {}
      ),
    }));

    const isValid = await validateStep();
    if (isValid) {
      handleContinue();
    }
  };

  const renderFieldLabel = (label, required = false) => (
    <Typography variant="subtitle2" sx={fieldLabelStyles}>
      {label}
      {required ? (
        <Box component="span" sx={{ color: 'error.main' }}>
          {' '}
          *
        </Box>
      ) : null}
    </Typography>
  );

  return (
    <Stack spacing={4} sx={{ maxWidth: 760, mx: 'auto' }}>
      <Box sx={{ width: '100%', px: { xs: 0, md: 1 }, pb: 1 }}>
        <SignupProgress steps={signupFlowSteps} activeStep={1} />
      </Box>

      <Paper
        elevation={0}
        sx={{
          px: { xs: 2.5, sm: 6, md: 8 },
          py: { xs: 2.5, sm: 6, md: 8 },
          borderRadius: 4,
          backgroundColor: '#ffffff',
          boxShadow: '0 18px 48px rgba(28, 27, 31, 0.10)',
          border: '1px solid #E4E6F1',
        }}
      >
        <Box sx={{ maxWidth: 560, mx: 'auto' }}>
          <Stack spacing={{ xs: 2.5, sm: 2 }}>
            <Stack spacing={1} alignItems="center" textAlign="center">
              <Typography
                variant="h4"
                sx={authHeadingStyles}
              >
                Address Details
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={authSubtextStyles}
              >
                Enter your address information
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                alignItems: 'start',
              }}
            >
              <Box sx={fieldGroupStyles}>
                {addressTemplate?.postalCode && renderFieldLabel('Postal Code', true)}
                <TextField
                  placeholder="Enter postal code"
                  value={formData?.pincode || ''}
                  onChange={handlePostalCodeChange}
                  error={touched.pincode && Boolean(errors.pincode)}
                  helperText={
                    touched.pincode && errors.pincode
                      ? errors.pincode
                      : (addressTemplate?.postalCode?.hint || getPatternExplanation(addressTemplate?.postalCode?.pattern))
                  }
                  inputProps={{
                    maxLength: extractedMaxLength || undefined,
                  }}
                />
              </Box>

              <Box sx={fieldGroupStyles}>
                {renderFieldLabel('Country')}
                <TextField
                  value={selectedCountryName}
                  placeholder="Country"
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Box>

            {addressTemplate?.levels && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                  alignItems: 'start',
                }}
              >
                {addressTemplate.levels.map((level) => {
                  const fieldName = levelFieldMap[level.level];
                  const options = mergedLevelOptions[level.level] || [];
                  const isLastLevel = level.level === addressTemplate.levels.length;
                  const parentLevel = addressTemplate.levels.find(
                    (candidate) => candidate.level === level.level - 1
                  );

                  // Determine if all parent levels have values
                  let parentLevelsComplete = true;
                  for (let i = 1; i < level.level; i++) {
                    const parentFieldName = levelFieldMap[i];
                    if (!parentFieldName || !formData?.[parentFieldName]) {
                      parentLevelsComplete = false;
                      break;
                    }
                  }

                  const isDisabled = !parentLevelsComplete;
                  const isLoading = levelLoadingStates[level.level] || false;
                  const hasDropdownOptions = options.length > 0;
                  const autocompleteValue = resolveLevelAutocompleteValue(
                    level.level,
                    fieldName,
                    options
                  );

                  return (
                    <Box key={level.level} sx={fieldGroupStyles}>
                      {renderFieldLabel(level.name, !isLastLevel && level.required !== false)}
                      <Autocomplete
                        freeSolo
                        autoHighlight
                        openOnFocus
                        selectOnFocus
                        handleHomeEndKeys
                        disabled={isDisabled}
                        loading={isLoading}
                        options={options}
                        getOptionLabel={(option) =>
                          typeof option === 'string' ? option : (option?.name ?? '')
                        }
                        isOptionEqualToValue={(option, value) => {
                          if (typeof option === 'string' || typeof value === 'string') {
                            const optionLabel = typeof option === 'string' ? option : option?.name;
                            const valueLabel = typeof value === 'string' ? value : value?.name;
                            return optionLabel === valueLabel;
                          }
                          return String(option?.id) === String(value?.id);
                        }}
                        value={autocompleteValue}
                        inputValue={formData?.[`${fieldName}Name`] || ''}
                        onInputChange={handleLevelInput(level.level)}
                        onChange={handleLevelSelect(level.level)}
                        onBlur={handleLevelBlur(level.level, fieldName, options)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={
                              isDisabled && parentLevel
                                ? `Select ${parentLevel.name} first`
                                : hasDropdownOptions
                                  ? `Select ${level.name}`
                                  : `Select or type ${level.name.toLowerCase()}`
                            }
                            error={touched[fieldName] && Boolean(errors[fieldName])}
                            helperText={
                              touched[fieldName] && errors[fieldName]
                                ? errors[fieldName]
                                : undefined
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isLoading ? (
                                    <CircularProgress color="inherit" size={18} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}

            <Box sx={fieldGroupStyles}>
              {renderFieldLabel('Address Line 1', true)}
              <TextField
                placeholder="Street address, P.O. box, company name"
                value={formData.addressLine1 || ''}
                onChange={handleTextFieldChange('addressLine1')}
                error={touched.addressLine1 && Boolean(errors.addressLine1)}
                helperText={
                  touched.addressLine1 && errors.addressLine1
                    ? errors.addressLine1
                    : undefined
                }
              />
            </Box>

            <Box sx={fieldGroupStyles}>
              {renderFieldLabel('Address Line 2')}
              <TextField
                placeholder="Apartment, building, floor, etc."
                value={formData.addressLine2 || ''}
                onChange={handleTextFieldChange('addressLine2')}
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                pt: 2,
              }}
            >
              <Button
                variant="outlined"
                size="large"
                onClick={handleBack}
                sx={{
                  order: { xs: 2, sm: 1 },
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(90, 143, 17, 0.06)',
                  },
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleContinueClick}
                sx={{ order: { xs: 1, sm: 2 } }}
              >
                Continue
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Stack>
  );
}
